import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'March 4, 2026';

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)', marginBottom: 12, letterSpacing: 0.5 }}>
        {title}
      </h2>
      <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: 15 }}>
        {children}
      </div>
    </div>
  );
}

function DataRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 16, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ minWidth: 200, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{label}</span>
      <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{value}</span>
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', padding: '48px 24px 80px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
            Legal
          </p>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>
            Privacy Policy
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            Last updated: {LAST_UPDATED}
          </p>
          <div style={{ height: 1, background: 'var(--border)', marginTop: 24 }} />
        </div>

        <Section title="Overview">
          <p>
            NeuroRank ("we", "us", "our") is committed to being transparent about what data we collect and how we use it. This policy explains our data practices in plain language. We do not sell your personal data to third parties.
          </p>
        </Section>

        <Section title="1. What Data We Collect">
          <p style={{ marginBottom: 16 }}>The table below summarises every category of data we collect:</p>
          <div style={{ borderTop: '1px solid var(--border)' }}>
            <DataRow label="Player tag" value="The display name you enter before starting a test. Publicly visible on your profile." />
            <DataRow label="Test session results" value="Raw performance data from each module (reaction times, click coordinates, accuracy). Used to compute your dimension scores." />
            <DataRow label="Computed scores" value="Your six dimension scores, overall score, and archetype. Publicly visible on your profile." />
            <DataRow label="AI-generated report" value="The narrative summary produced by our AI. Stored and displayed on your profile." />
            <DataRow label="Genre selection" value="General, FPS, or MOBA. Stored on your profile and used by scout filters." />
            <DataRow label="Email address" value="Only collected if you create an account. Used for login and security. Never publicly displayed." />
            <DataRow label="Password (hashed)" value="Stored as a one-way bcrypt hash. We cannot recover your plain-text password." />
            <DataRow label="Role & region" value="Optional fields on claimed profiles. Visible to scouts on the dashboard." />
            <DataRow label="Open to Opportunities" value="A toggle you control. When enabled, your profile appears in scout searches." />
            <DataRow label="Bookmarks & shortlist" value="Which profiles you have saved. Private to your account." />
            <DataRow label="OAuth tokens" value="If you link Discord, Steam, or Twitch, we store your username and platform user ID from that service. We do not store OAuth access tokens beyond the linking flow." />
            <DataRow label="Session metadata" value="Timestamps, session IDs, and browser-reported data needed to run the test. Not publicly exposed." />
          </div>
          <p style={{ marginTop: 16 }}>
            We do <strong style={{ color: 'var(--text-primary)' }}>not</strong> collect IP addresses for tracking, use third-party advertising trackers, or process payment information.
          </p>
        </Section>

        <Section title="2. How We Use Your Data">
          <ul style={{ paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <li><strong style={{ color: 'var(--text-primary)' }}>Profile generation:</strong> Raw test data is scored and converted into your neural profile. Raw trial-level data is not stored after scoring is complete.</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Norm calibration:</strong> Anonymised, aggregated score data is used to improve the accuracy of our percentile tables over time. This data cannot be linked back to individual profiles.</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Scout discovery:</strong> If your profile is public, your scores, archetype, genre, role, and region are returned in scout dashboard searches.</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>AI report generation:</strong> Your computed dimension scores are sent to the Anthropic Claude API to generate your written profile report. No personally identifiable information (email, IP) is included in this request.</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Authentication:</strong> Your email and hashed password are used solely for login. We do not use your email for marketing without explicit consent.</li>
          </ul>
        </Section>

        <Section title="3. Profile Visibility">
          <p>All profiles are <strong style={{ color: 'var(--text-primary)' }}>publicly accessible by default</strong> via their unique link. Anyone with the link can view your player tag, scores, archetype, and AI report.</p>
          <p style={{ marginTop: 12 }}>If you claim your profile by creating an account, you gain control over:</p>
          <ul style={{ marginTop: 8, paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Whether your profile appears in scout searches ("Open to Opportunities" toggle)</li>
            <li>Your role and region metadata displayed to scouts</li>
          </ul>
          <p style={{ marginTop: 12 }}>Full profile privacy controls (public / unlisted / private) are on our roadmap and will be available in a future update.</p>
        </Section>

        <Section title="4. Third-Party Services">
          <p>We use the following third-party services:</p>
          <ul style={{ marginTop: 12, paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <li><strong style={{ color: 'var(--text-primary)' }}>Anthropic Claude API:</strong> Used to generate AI profile reports. Anonymised score data only is sent. Anthropic's privacy policy governs their handling of API data.</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Discord / Steam / Twitch OAuth:</strong> Used optionally to link your gaming accounts. We receive only your username and platform user ID from these services.</li>
          </ul>
          <p style={{ marginTop: 12 }}>We do not use Google Analytics, Facebook Pixel, or any other advertising or behavioural tracking service.</p>
        </Section>

        <Section title="5. Data Retention">
          <ul style={{ paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <li><strong style={{ color: 'var(--text-primary)' }}>Unclaimed profiles:</strong> Retained indefinitely so the shareable link remains valid. You can request deletion by contacting us with your profile ID.</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Claimed profiles & accounts:</strong> Retained until you request deletion. Deleting your account does not automatically delete linked profiles — these must be requested separately.</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Raw trial data:</strong> Discarded after scoring. We do not store the individual click positions or timestamps from your test session long-term.</li>
          </ul>
        </Section>

        <Section title="6. Your Rights">
          <p>You have the right to:</p>
          <ul style={{ marginTop: 12, paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li><strong style={{ color: 'var(--text-primary)' }}>Access</strong> the data we hold about you</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Correct</strong> inaccurate information on your profile</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Delete</strong> your profile and/or account</li>
            <li><strong style={{ color: 'var(--text-primary)' }}>Object</strong> to your data being used in anonymised norm aggregation</li>
          </ul>
          <p style={{ marginTop: 12 }}>To exercise any of these rights, contact us with your player tag or profile ID. We will respond within 30 days.</p>
        </Section>

        <Section title="7. Children's Privacy">
          <p>NeuroRank is not directed at children under 13. We do not knowingly collect personal data from anyone under 13. If you believe a child under 13 has submitted data to our platform, please contact us and we will delete it promptly.</p>
        </Section>

        <Section title="8. Security">
          <p>Passwords are hashed using bcrypt before storage. JWT tokens are signed and have limited lifetimes. We use HTTPS for all data in transit. Our platform does not store credit card or payment information.</p>
          <p style={{ marginTop: 12 }}>No system is perfectly secure. If you discover a security vulnerability, please report it to us responsibly before public disclosure.</p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>We may update this Privacy Policy as the platform evolves. We will update the "Last updated" date when changes are made. Continued use of the platform after changes are posted constitutes acceptance.</p>
        </Section>

        <Section title="10. Contact">
          <p>For privacy requests, data deletion, or questions about this policy, contact us via the platform or open an issue at our public repository. Include your player tag or profile ID so we can locate your data.</p>
        </Section>

        {/* Footer nav */}
        <div style={{ height: 1, background: 'var(--border)', margin: '40px 0 32px' }} />
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Link to="/" style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}>← Back to Home</Link>
          <Link to="/terms" style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}>Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}
